import React from 'react';
import { systemMonitoringService } from '../../services/systemMonitoringService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Server, Database, Activity, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

const HealthStatusOverview = ({ healthData, metricsData }) => {
    const { t } = useLanguage();

    const healthStatus = systemMonitoringService.calculateSystemHealthStatus(healthData, metricsData);

    const getStatusConfig = (status) => {
        const configs = {
            optimal: {
                icon: CheckCircle,
                color: '#10B981',
                bgColor: 'rgba(16, 185, 129, 0.1)',
                text: t('systemHealthSystemOptimal')
            },
            degraded: {
                icon: AlertTriangle,
                color: '#F59E0B',
                bgColor: 'rgba(245, 158, 11, 0.1)',
                text: t('systemHealthSystemDegraded')
            },
            critical: {
                icon: XCircle,
                color: '#EF4444',
                bgColor: 'rgba(239, 68, 68, 0.1)',
                text: t('systemHealthSystemCritical')
            },
            unknown: {
                icon: Clock,
                color: '#6B7280',
                bgColor: 'rgba(107, 114, 128, 0.1)',
                text: 'Durum Bilinmiyor'
            }
        };
        return configs[status] || configs.unknown;
    };

    const statusConfig = getStatusConfig(healthStatus);
    const StatusIcon = statusConfig.icon;

    const SystemMetricCard = ({ icon: Icon, label, value, unit, trend, status = 'optimal' }) => {
        const trendIcons = {
            increasing: '↗️',
            decreasing: '↘️',
            stable: '→'
        };

        const statusColors = {
            optimal: '#10B981',
            warning: '#F59E0B',
            critical: '#EF4444'
        };

        return (
            <div className="system-metric-card">
                <div className="metric-header">
                    <div className="metric-icon">
                        <Icon size={20} />
                    </div>
                    <span className="metric-label">{label}</span>
                    <span className="metric-trend">{trendIcons[trend]}</span>
                </div>
                <div className="metric-value" style={{ color: statusColors[status] }}>
                    {value} <span className="metric-unit">{unit}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="health-status-overview">
            <div className="status-header">
                <div className="status-badge" style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}>
                    <StatusIcon size={20} />
                    <span>{statusConfig.text}</span>
                </div>
                <div className="status-metrics">
                    <div className="uptime-metric">
                        <Clock size={16} />
                        <span>Uptime: {healthData?.uptime ? `${Math.floor(healthData.uptime / 3600)}h ${Math.floor((healthData.uptime % 3600) / 60)}m` : 'N/A'}</span>
                    </div>
                </div>
            </div>

            {metricsData?.simulated && (
                <div
                    className="status-badge"
                    style={{ backgroundColor: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', marginBottom: '12px' }}
                >
                    <AlertTriangle size={16} />
                    <span>Bu metrikler henüz gerçek zamanlı izlenmiyor (simüle edilmemiş, boş görüntüleniyor)</span>
                </div>
            )}

            <div className="system-metrics-grid">
                <SystemMetricCard
                    icon={Server}
                    label={t('systemHealthCPUUsage')}
                    value={metricsData?.cpuUtilization ?? '—'}
                    unit="%"
                    trend="stable"
                    status={metricsData?.cpuUtilization > 80 ? 'critical' : metricsData?.cpuUtilization > 60 ? 'warning' : 'optimal'}
                />
                <SystemMetricCard
                    icon={Activity}
                    label={t('systemHealthMemoryUsage')}
                    value={metricsData?.memoryUsage ?? '—'}
                    unit="%"
                    trend="stable"
                    status={metricsData?.memoryUsage > 85 ? 'critical' : metricsData?.memoryUsage > 70 ? 'warning' : 'optimal'}
                />
                <SystemMetricCard
                    icon={Database}
                    label={t('systemHealthDiskUsage')}
                    value={metricsData?.diskUsage ?? '—'}
                    unit="%"
                    trend="increasing"
                    status={metricsData?.diskUsage > 90 ? 'critical' : metricsData?.diskUsage > 80 ? 'warning' : 'optimal'}
                />
                <SystemMetricCard
                    icon={Activity}
                    label={t('systemHealthResponseLatency')}
                    value={metricsData?.responseLatency ?? '—'}
                    unit="ms"
                    trend="stable"
                    status={metricsData?.responseLatency > 500 ? 'critical' : metricsData?.responseLatency > 200 ? 'warning' : 'optimal'}
                />
            </div>

            <div className="infrastructure-status">
                <h3>{t('systemHealthInfrastructureStatus')}</h3>
                <div className="infrastructure-grid">
                    <div className={`infra-item ${healthData?.database?.healthy ? 'healthy' : 'critical'}`}>
                        <Database size={16} />
                        <span>Database</span>
                        <div className="status-dot"></div>
                    </div>
                    <div className="infra-item healthy">
                        <Server size={16} />
                        <span>API Server</span>
                        <div className="status-dot"></div>
                    </div>
                    <div className="infra-item healthy">
                        <Activity size={16} />
                        <span>Cache Layer</span>
                        <div className="status-dot"></div>
                    </div>
                    <div className="infra-item healthy">
                        <Activity size={16} />
                        <span>File Storage</span>
                        <div className="status-dot"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HealthStatusOverview;